import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, IdCard, Pencil } from "lucide-react";

import Button from "../../../../shared/ui/button/Button";
import { Surface } from "../../../../shared/ui/primitives";
import {
    validateCustomValue,
    type PatientCustomField,
    type PatientCustomValues,
    type PatientCustomValuesInput,
} from "../../domain/PatientCustomField";
import { customFieldRefusal } from "../customFieldRefusals";
import CustomFieldInput from "./CustomFieldInput";

interface Props {
    data?: PatientCustomValues;
    isLoading: boolean;
    isError: boolean;
    onRetry: () => void;
    /** Saves the values that changed; a rejection is shown on the form. */
    onSave: (values: PatientCustomValuesInput) => Promise<unknown>;
    isSaving: boolean;
    /** The card's heading level where it sits: a page section, or inside a dialog. */
    headingLevel?: "h2" | "h3" | "h4";
    className?: string;
}

/** A value as read: a date in the reader's language, anything else as stored. */
function displayValue(field: PatientCustomField, value: string | undefined, locale: string): string | null {
    if (!value) return null;
    if (field.field_type === "date") {
        const day = new Date(`${value}T00:00:00`);
        return Number.isNaN(day.getTime())
            ? value
            : day.toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });
    }
    return value;
}

/**
 * The centre's own fields for one patient: a student number, a file
 * reference. Read as a list, edited in place. Nothing at all when the centre
 * defines no fields — or the patient belongs to no centre.
 */
export default function CustomFieldsCard({
    data,
    isLoading,
    isError,
    onRetry,
    onSave,
    isSaving,
    headingLevel = "h3",
    className,
}: Props) {
    const { t, i18n } = useTranslation();
    const Heading = headingLevel;
    const idPrefix = useId();
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState<Record<string, string>>({});
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [formError, setFormError] = useState<string | null>(null);

    if (!isLoading && !isError && !data?.fields.length) return null;

    const title = (
        <Heading className="flex items-center gap-2 text-base font-semibold text-content-primary">
            <IdCard className="h-5 w-5 text-content-tertiary" aria-hidden="true" />
            {t("center.patientFields.card.title")}
        </Heading>
    );

    if (isLoading || isError || !data) {
        return (
            <Surface as="section" className={className}>
                {title}
                {isError ? (
                    <div role="alert" className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm">
                        <span className="text-status-critical">{t("center.patientFields.card.errorLoading")}</span>
                        <Button size="sm" variant="outline" onClick={onRetry}>
                            {t("center.patientFields.card.retry")}
                        </Button>
                    </div>
                ) : (
                    <p role="status" className="mt-3 text-sm text-content-secondary">
                        {t("common.loading")}
                    </p>
                )}
            </Surface>
        );
    }

    const { fields, values } = data;

    const startEditing = () => {
        setDraft(Object.fromEntries(fields.map((field) => [field.id, values[field.id] ?? ""])));
        setFieldErrors({});
        setFormError(null);
        setEditing(true);
    };

    const change = (fieldId: string, value: string) => {
        setDraft((current) => ({ ...current, [fieldId]: value }));
        setFieldErrors((current) => {
            if (!current[fieldId]) return current;
            const next = { ...current };
            delete next[fieldId];
            return next;
        });
    };

    const handleSubmit = async () => {
        setFormError(null);
        const errors: Record<string, string> = {};
        for (const field of fields) {
            const problem = validateCustomValue(field, draft[field.id] ?? "");
            if (problem) errors[field.id] = problem;
        }
        setFieldErrors(errors);
        if (Object.keys(errors).length > 0) return;

        // Only what changed is sent: the server treats absent ids as untouched.
        const changes: PatientCustomValuesInput = {};
        for (const field of fields) {
            const next = (draft[field.id] ?? "").trim();
            if (next !== (values[field.id] ?? "")) changes[field.id] = next || null;
        }
        if (Object.keys(changes).length === 0) {
            setEditing(false);
            return;
        }
        try {
            await onSave(changes);
            setEditing(false);
        } catch (err) {
            const refusal = customFieldRefusal(err);
            if (refusal.fieldId && fields.some((field) => field.id === refusal.fieldId)) {
                setFieldErrors({ [refusal.fieldId]: refusal.message });
            } else {
                setFormError(refusal.message);
            }
        }
    };

    if (editing) {
        return (
            <Surface as="section" className={className}>
                <form
                    noValidate
                    className="flex flex-col gap-4"
                    onSubmit={(e) => {
                        e.preventDefault();
                        void handleSubmit();
                    }}
                >
                    {title}
                    <div className="grid gap-4 sm:grid-cols-2">
                        {fields.map((field) => (
                            <CustomFieldInput
                                key={field.id}
                                field={field}
                                id={`${idPrefix}-${field.id}`}
                                value={draft[field.id] ?? ""}
                                onChange={(value) => change(field.id, value)}
                                error={fieldErrors[field.id]}
                            />
                        ))}
                    </div>
                    {formError && (
                        <p role="alert" className="text-sm text-status-critical">
                            {t(formError)}
                        </p>
                    )}
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setEditing(false)} disabled={isSaving}>
                            {t("common.cancel")}
                        </Button>
                        <Button type="submit" loading={isSaving} disabled={isSaving}>
                            {t("common.save")}
                        </Button>
                    </div>
                </form>
            </Surface>
        );
    }

    return (
        <Surface as="section" className={className}>
            <div className="flex items-start justify-between gap-3">
                {title}
                <Button
                    size="sm"
                    variant="outline"
                    onClick={startEditing}
                    startIcon={<Pencil size={14} aria-hidden="true" />}
                >
                    {t("center.patientFields.card.edit")}
                </Button>
            </div>
            <dl className="mt-3 divide-y divide-border-subtle">
                {fields.map((field) => {
                    const shown = displayValue(field, values[field.id], i18n.language);
                    return (
                        <div
                            key={field.id}
                            className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-3 py-2.5 text-sm"
                        >
                            <dt className="text-content-secondary">{field.label}</dt>
                            <dd className="flex flex-wrap items-center gap-2 break-words text-content-primary">
                                {shown ?? <span className="text-content-tertiary">—</span>}
                                {!shown && field.required && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-status-warning-tint px-2.5 py-1 text-xs font-medium text-status-warning-on-tint">
                                        <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                                        {t("center.patientFields.card.missing")}
                                    </span>
                                )}
                            </dd>
                        </div>
                    );
                })}
            </dl>
        </Surface>
    );
}
